import type { Space } from '@cocoonspace/sdk-js/types/space'
import { bookingConfig } from '@cocoonspace/shared/config/booking-config'
import { capitalize } from '@cocoonspace/shared/utils/capitalize'
import { recurringSpacePrice } from '@cocoonspace/shared/utils/recurring-space-price'
import { getRecurringSpaceTypeformURLPricingSection } from '@cocoonspace/shared/utils/recurring-space-typeform'
import { Trans, useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useMemo } from 'react'
import { Price } from '~/components/price'
import { cardVariants } from '~/components/ui/card'
import { useTracking } from '~/providers/tracking-provider'

export const SpacePricesRecurring = ({
	space,
}: { space: Pick<Space, 'title' | 'name' | 'prices'> }) => {
	const { t } = useTranslation()
	const { track } = useTracking()

	const offers = useMemo(() => {
		return bookingConfig.recurring.offers.map((offer) => {
			const offerPrice = recurringSpacePrice({
				space,
				rebate: offer.rebate,
				billedHours: offer.billedHours,
				label: 'days',
				daysPerWeek: offer.daysPerWeek,
			})

			return {
				label: 'days' as const,
				daysPerWeek: offer.daysPerWeek,
				rebate: Math.floor(offer.rebate * 100),
				ogPrice: Math.round(offerPrice.ogPrice / 100) * 100,
				price: Math.round(offerPrice.recurringPrice / 100) * 100,
			}
		})
	}, [space])

	return (
		<div>
			<div className='grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2'>
				<div>
					<div className='mb-4 text-justify'>
						<Trans i18nKey='space:sections.prices.pricings.recurring.body' />
					</div>

					<div className='text-justify'>
						<Trans i18nKey='space:sections.prices.pricings.recurring.body2' />
					</div>
				</div>

				<div className='flex flex-col gap-y-2'>
					{offers?.map((offer) => (
						<Link
							key={offer.daysPerWeek}
							href={getRecurringSpaceTypeformURLPricingSection({
								space,
								prices: offers.map((offer) =>
									(offer.price / 100).toFixed(2).replace('.', ','),
								),
							})}
							target='_blank'
							className={cardVariants({
								className:
									'flex flex-col gap-y-3 p-4 shadow-[inset] transition-shadow hover:shadow-[inset_0_0_0_2px_#ff4338]',
							})}
							onClick={() =>
								track.recurringOffer({
									source: 'tarifs',
									cocoon: space.name,
									type: offer.label,
								})
							}
						>
							<div className='flex items-end justify-between leading-none'>
								<span className='font-bold text-sm'>
									{t(`space:sections.prices.pricings.recurring.offers.title`, {
										count: offer.daysPerWeek,
									})}
								</span>

								<span className='flex items-end gap-x-2'>
									<span className='mb-px font-normal text-xs opacity-60'>
										{capitalize(t('common:from'))}
									</span>

									<Price
										price={offer.price}
										className='text-primary'
										suffix='monthlyLong'
										taxType='ht'
									/>
								</span>
							</div>

							<Trans
								className='text-sm'
								parent='p'
								i18nKey={`space:sections.prices.pricings.recurring.offers.description`}
								values={{
									count: offer.daysPerWeek,
									rebate: offer.rebate,
								}}
							/>
						</Link>
					))}
				</div>
			</div>
		</div>
	)
}
